import { useRef, useState } from "react";

import { ManualResultsApiClient } from "@/Common/Api/ManualResults/ManualResultsApiClient";
import { LabAnalysisModel } from "@/Common/Models/ManualLabResultsEntry/LabAnalysisModel";
import { ISimpleDisplayModel, SimpleDisplayModel, SimpleSearchComplete } from "../Search/SimpleSearchAutocomplete";

interface SearchLabAnalysisDropdownProps {
    dataCy: string;
    item: LabAnalysisModel | null;
    onItemSelected: (item: LabAnalysisModel | null) => void;
    id: string;
}

function SearchLabAnalysisDropdown({
    dataCy,
    item,
    onItemSelected,
    id
}: Readonly<SearchLabAnalysisDropdownProps>): JSX.Element {
    const componentRef = useRef<SimpleSearchComplete<LabAnalysisModel>>(null);
    const [searchText, setSearchText] = useState<string>(item?.getLabel() ?? "");
    const [valid, setValid] = useState<boolean>(true);

    async function onSearch(searchText: string): Promise<SimpleDisplayModel<LabAnalysisModel>[]> {
        if (!searchText || searchText.length < 3)
            return [];

        const fetchedOptions = await ManualResultsApiClient.fetchFilteredLabAnalysisDefinitions(searchText);
        const definitions = fetchedOptions.Definitions.map(dto => new LabAnalysisModel(dto));

        if (definitions.length === 0)
            setValid(false);

        return definitions.map(
            (analysis, index) => new SimpleDisplayModel({
                Item: {
                    Id: analysis.getId(),
                    DisplayText: analysis.getLabel(),
                    Data: analysis
                },
                Active: index === 0
            })
        );
    }

    function handleItemSelected(item?: ISimpleDisplayModel<LabAnalysisModel>): void {
        setValid(!!item);
        onItemSelected(item?.Data ?? null);
    }

    return (
        <SimpleSearchComplete<LabAnalysisModel>
            ref={componentRef}
            dataCy={dataCy}
            componentId={id}
            selectedItemList={(item ? [{ DisplayText: item.getLabel(), Id: item.getId() }] : []).toImmutable()}
            onSelectionCallback={({ Item }) => handleItemSelected(Item)}
            closeDropDownAfterSelection
            doNotEraseListAfterSelection
            useBlurEvent={false}
            value={searchText}
            onTextChange={searchString => setSearchText(searchString)}
            valid={valid}
            onSearch={onSearch}
            onClearCallback={() => handleItemSelected(null)}
        />
    );
}

export { SearchLabAnalysisDropdown };
